import type { CreateOnDictaphoneButtonPress } from '../../types';
import type { DictaphoneControlsPluginPropertyOptions } from './types';
import { unreachableCaseError } from 'types';
import {
  getLeftChar,
  getRightChar,
  getWordRangeAtPoint,
  isWordChar,
  moveSelectionToEndOfLine,
  navigateForward,
  navigateBack,
} from '../../utils';
import { BRACKET_TYPES } from '../../constants';
import type { GamepadActionId } from 'generated/graphql';
import {
  measureReportNavigationEnd,
  ReportNavigationDirection,
  ReportNavigationMethod,
} from '../../analytics/performance';
import { Editor, Element, Range, Transforms } from '../../core';
import { HEADING_PLUGIN_ID } from '../heading/types';
import { createParagraph } from '../paragraph/utils';
import { insertInlineTemplateLineBreak } from '../common/inlineTemplate';

const handleNavigateForward = (editor: Editor, ignoreMergeFieldsInNavigation?: boolean) => {
  navigateForward({ editor, ignoreMergeFieldsInNavigation });

  measureReportNavigationEnd({
    direction: ReportNavigationDirection.Forward,
    method: ReportNavigationMethod.ControlInputDevice,
  });
};

const handleNavigateBack = (editor: Editor, ignoreMergeFieldsInNavigation?: boolean) => {
  navigateBack({ editor, ignoreMergeFieldsInNavigation });

  measureReportNavigationEnd({
    direction: ReportNavigationDirection.Back,
    method: ReportNavigationMethod.ControlInputDevice,
  });
};

const handleDeleteWordBackward = (editor: Editor) => {
  if (editor.selection != null && Range.isCollapsed(editor.selection)) {
    const point = editor.selection.anchor;
    const leftChar = getLeftChar(editor, point);
    const rightChar = getRightChar(editor, point);

    // Dictaphone delete-word should remove the active word when the cursor is inside it.
    if (isWordChar(leftChar) && isWordChar(rightChar)) {
      Transforms.delete(editor, { at: getWordRangeAtPoint(editor, point) });
      return;
    }
  }

  if (editor.selection != null) {
    const selection = editor.selection;

    if (!Range.isCollapsed(selection)) {
      const [start, end] = Range.edges(selection);
      const isBackwardSelection = Range.isBackward(selection);

      const expandedStart = isWordChar(getRightChar(editor, start))
        ? getWordRangeAtPoint(editor, start).anchor
        : start;
      const expandedEnd = isWordChar(getLeftChar(editor, end))
        ? getWordRangeAtPoint(editor, end).focus
        : end;

      Transforms.delete(editor, {
        at: {
          anchor: isBackwardSelection ? expandedEnd : expandedStart,
          focus: isBackwardSelection ? expandedStart : expandedEnd,
        },
      });

      return;
    }
  }

  Transforms.delete(editor, { unit: 'word', reverse: true });
};

const handleExitField = (editor: Editor) => {
  if (editor.selection == null) {
    return;
  }

  const activeField = Editor.above(editor, {
    at: editor.selection,
    match: (n) => Element.isElement(n) && BRACKET_TYPES.includes(n.type),
    mode: 'lowest',
  });

  if (activeField == null) {
    return;
  }

  const [, activeFieldPath] = activeField;
  const pointAfterBracket = Editor.after(editor, activeFieldPath) ?? Editor.end(editor, []);
  Transforms.select(editor, pointAfterBracket);
};

const isSelectionInHeading = (editor: Editor) => {
  if (editor.selection == null) {
    return false;
  }

  const nodeEntry = Editor.node(editor, editor.selection, { depth: 1 });
  return (
    nodeEntry != null && Element.isElement(nodeEntry[0]) && nodeEntry[0].type === HEADING_PLUGIN_ID
  );
};

const isSelectionInBracketType = (editor: Editor) => {
  if (editor.selection == null) {
    return false;
  }

  const nodes = Editor.nodes(editor, {
    at: editor.selection,
    match: (n) => Element.isElement(n) && BRACKET_TYPES.includes(n.type),
    mode: 'lowest',
  });

  return !nodes.next().done;
};

const handleNewLine = (editor: Editor) => {
  // Keep dictaphone "new line" behavior aligned with keyboard Enter in headings.
  if (isSelectionInHeading(editor)) {
    Transforms.insertNodes(editor, createParagraph());
    return;
  }

  // Keep dictaphone "new line" behavior aligned with keyboard Enter in inline templates.
  if (isSelectionInBracketType(editor)) {
    insertInlineTemplateLineBreak(editor);
    return;
  }

  Editor.insertBreak(editor);
};

export const onDictaphoneButtonPressDictaphoneControls: CreateOnDictaphoneButtonPress<
  DictaphoneControlsPluginPropertyOptions
> =
  ({ hotkey, pluginID, ignoreMergeFieldsInNavigation }) =>
  (action: GamepadActionId, { editor }) => {
    switch (action) {
      case 'forward':
        handleNavigateForward(editor, ignoreMergeFieldsInNavigation);
        return false;
      case 'back':
        handleNavigateBack(editor, ignoreMergeFieldsInNavigation);
        return false;
      case 'exitField':
        handleExitField(editor);
        return false;
      case 'deleteForward':
        Transforms.delete(editor, { unit: 'character' });
        return false;
      case 'deleteBackward':
        Transforms.delete(editor, { unit: 'character', reverse: true });
        return false;
      case 'deleteWordBackward':
        handleDeleteWordBackward(editor);
        return false;
      case 'newLine':
        handleNewLine(editor);
        return false;
      case 'endOfLine':
        moveSelectionToEndOfLine(editor);
        return false;
      case 'endOfReport':
        Transforms.select(editor, Editor.end(editor, []));
        return false;
      case 'space':
        Transforms.insertText(editor, ' ');
        return false;
      case 'undo':
        editor.undo();
        return false;
      case 'draftReport':
        // This is handled in Fields.Context, so that the FieldsProvider does not need to be lifted further up the tree
        return false;
      case 'submitPrelimReport':
        // This is handled in Fields.Context, so that the FieldsProvider does not need to be lifted further up the tree
        return false;
      case 'generateImpression':
        // This is handled in Fields.Context, so that the FieldsProvider does not need to be lifted further up the tree
        return false;
      case 'record':
        return false;
      case 'submitReport':
        // This is handled in Fields.Context, so that the FieldsProvider does not need to be lifted further up the tree
        return false;
      case 'toggleFocusMode':
        return false;
      case 'toggleVisibility':
        // This is handled in Fields.Context, so that the FieldsProvider does not need to be lifted further up the tree
        return false;
      case 'commandMode':
        return false;
      default:
        unreachableCaseError(action);
    }
  };
